import { Component, createRef, type ReactNode, type RefObject } from 'react';
import { Image, Text, VStack, HStack } from '@chakra-ui/react';
import * as THREE from 'three';
import { useLocation, useNavigate, type NavigateFunction } from 'react-router-dom';
import Loading from './components/loading';
import logo_skills from './components/skills.png';
import logo_project from './components/project_img.png';
import logo_contact from './components/contact.png';
import logo_blogs from './components/blogs_img.png';
import type { ParentCallback, ParentCallbackTwo, WindowFlagName } from './types/window';
import {
  buildWindowsPath,
  closeRouteInList,
  FLAG_TO_ROUTE,
  openRouteInList,
  parseOpenSegments,
  toggleRouteInList,
  visibilityFromPath,
  type OpenSegment,
  type WindowRouteId,
} from './routing/windows';
import {
  ArcadeGame,
  MISSILE_MAX,
  RAILGUN_MAX,
  CHAFF_MAX,
  OBSTACLE_LIMIT_DEFAULT,
  OBSTACLE_LIMIT_MAX,
  OBSTACLE_LIMIT_MIN,
  ENEMY_SPAWN_MAX,
  TEAMMATE_SPAWN_MAX,
  DEFAULT_SHIP,
  DEFAULT_TERRAIN,
  SceneEnvironment,
  shipLabel,
  terrainLabel,
  type GamePhase,
  type ShipId,
  type TerrainId,
  type WeaponId,
  playerMissileLabel,
  playerSecondaryWeaponLabel,
  playerCountermeasureLabel,
} from './game';
import Settings from './features/settings';
import { ArcadeHud, PauseOverlay, type PauseUiPhase } from './features/arcade';
import { bindArcadeInput, bindDeviceOrientation } from './features/arcade/input';
import { startRenderLoop } from './features/arcade/runtime';
import DesktopShell, { type DesktopIconId } from './features/desktop';
import {
  Skills,
  Projects,
  Contact,
  Blogs,
  Credits,
  GameOverWindow,
  TerrainPicker,
  ShipsPicker,
  BlogPostWindow,
} from './features/windows';
import { createArcadeScene } from './app/boot';
import './App.css';
import './game/ArcadeHud.css';
import {
  availableModes,
  defaultGameMode,
  isFeatureEnabled,
  isSandboxLikeMode,
  modeBadgeLabel,
  modeMenuLabel,
  resolveGameMode,
  type GameModeId,
} from './shared/config';

/** Desktop icon id → URL route segment */
const DESKTOP_ICON_TO_ROUTE: Record<DesktopIconId, WindowRouteId> = {
  skills: 'about', // ABOUT panel
  projects: 'projects',
  about: 'contact', // CONTACT panel (legacy icon id)
  blogs: 'blogs',
};

const DESKTOP_ICONS = [
  { id: 'skills' as const, label: 'ABOUT', iconSrc: logo_skills },
  { id: 'projects' as const, label: 'PROJECTS', iconSrc: logo_project },
  { id: 'about' as const, label: 'CONTACT', iconSrc: logo_contact },
  { id: 'blogs' as const, label: 'BLOGS', iconSrc: logo_blogs },
];

interface AppProps {
  navigate: NavigateFunction;
  pathname: string;
}

interface AppState {
  name: string;
  posts: unknown[];
  currentTime: Date;
  loadingComplete: boolean;
  showHideSkills: boolean;
  showHideProjects: boolean;
  showHideAbout: boolean;
  showHideBlogs: boolean;
  showHideCredits: boolean;
  showMenu: boolean;
  /** Flyout next to Start menu: Regular / Sandbox */
  showModesSubmenu: boolean;
  selectedDesktopIcon: DesktopIconId | null; // features/desktop
  /** Desktop idle vs endless corridor arcade */
  gamePhase: GamePhase;
  /** Regular (scored, limited ammo) vs Sandbox (∞ ammo) */
  gameMode: GameModeId;
  /** Final survival score (ms) shown on the game-over panel */
  lastScoreMs: number;
  /** Missiles remaining (0…MISSILE_MAX) for the HUD */
  ammoRemaining: number;
  /** Railgun rounds remaining */
  railAmmo: number;
  /** Chaff pods remaining (0…CHAFF_MAX) for the HUD */
  chaffAmmo: number;
  /** Active weapon for HUD highlight */
  activeWeapon: WeaponId;
  /** Sandbox: any enemy currently alive (spawn button disabled) */
  enemyAlive: boolean;
  /** Sandbox: how many enemies to spawn at once (1–ENEMY_SPAWN_MAX) */
  enemySpawnCount: number;
  /** Sandbox: any teammate currently alive (spawn button disabled) */
  teammateAlive: boolean;
  /** Sandbox: how many teammates to spawn at once (1–TEAMMATE_SPAWN_MAX) */
  teammateSpawnCount: number;
  /** Sandbox: player immortal / god-mode */
  immortal: boolean;
  /** Missile auto-aim at obstacle blocks (preference; default on). */
  blockAutoAim: boolean;
  /** In-run settings window (not URL-routed) */
  showSettings: boolean;
  /** Pause overlay phase while playing */
  pauseUi: PauseUiPhase;
  /** 3-2-1 resume countdown value */
  countdown: number | null;
  /** Stacking order for the settings WindowFrame */
  settingsZ: number;
  /** Sandbox: whether obstacle spawning is on */
  obstaclesEnabled: boolean;
  /** Sandbox: max simultaneous obstacle blocks */
  obstacleLimit: number;
  /** Sandbox garage: environment + craft */
  terrainId: TerrainId;
  shipId: ShipId;
  showHideTerrain: boolean;
  showHideShips: boolean;
  refMenu: RefObject<HTMLDivElement>;
  refMenuCard: RefObject<HTMLDivElement>;
  refModesSubmenu: RefObject<HTMLDivElement>;
  /** Live score readout (updated without full React re-renders) */
  refScoreValue: RefObject<HTMLSpanElement>;
  /** Sandbox obstacle count label (live/max) — HUD stack */
  refObstacleLimitLabel: RefObject<HTMLSpanElement>;
  /** Obstacle count label inside Settings → Game */
  refSettingsObstacleLimitLabel: RefObject<HTMLSpanElement>;
  /** Missile lock square (positioned each frame) */
  refMissileLock: RefObject<HTMLDivElement>;
  /** Enemy lock square (orange soft / red hard) */
  refEnemyLock: RefObject<HTMLDivElement>;
  /** Incoming hostile missile lock warning */
  refThreatLock: RefObject<HTMLDivElement>;
  z_skills: number;
  z_about: number;
  z_projects: number;
  z_blogs: number;
  z_credits: number;
  z_terrain: number;
  z_ships: number;
  openBlogSlugs: string[];
}

class App extends Component<AppProps, AppState> {
  clockInterval: ReturnType<typeof setInterval> | null = null;
  mount: HTMLDivElement | null = null;
  arcade: ArcadeGame | null = null;
  /** Prevents double Three.js boot (Strict Mode / double loading complete). */
  private sceneBooted = false;
  private renderer: THREE.WebGLRenderer | null = null;
  private disposeArcadeInput: (() => void) | null = null;
  private disposeOrientation: (() => void) | null = null;
  private stopRenderLoop: (() => void) | null = null;
  private boundMouseMove: ((e: MouseEvent) => void) | null = null;
  private mobileTouchStartAt = 0;
  /** Suppress click-after-touch so long-press lock doesn’t cycle weapons. */
  private lastTouchEndAt = 0;
  private orientationPermission: 'unknown' | 'granted' | 'denied' = 'unknown';
  private sceneEnv: SceneEnvironment | null = null;
  private countdownTimer: ReturnType<typeof setInterval> | null = null;

  constructor(props: AppProps) {
    super(props);
    const fromPath = visibilityFromPath(props.pathname);
    this.state = {
      name: 'React',
      posts: [],
      currentTime: new Date(),
      loadingComplete: false,
      ...fromPath,
      showMenu: false,
      showModesSubmenu: false,
      selectedDesktopIcon: null,
      gamePhase: 'idle',
      gameMode: defaultGameMode(),
      lastScoreMs: 0,
      ammoRemaining: MISSILE_MAX,
      railAmmo: RAILGUN_MAX,
      chaffAmmo: CHAFF_MAX,
      activeWeapon: 'missile',
      enemyAlive: false,
      enemySpawnCount: 1,
      teammateAlive: false,
      teammateSpawnCount: 1,
      immortal: false,
      blockAutoAim: true,
      showSettings: false,
      pauseUi: 'none',
      countdown: null,
      settingsZ: 50,
      obstaclesEnabled: true,
      obstacleLimit: OBSTACLE_LIMIT_DEFAULT,
      terrainId: DEFAULT_TERRAIN,
      shipId: DEFAULT_SHIP,
      refMenu: createRef<HTMLDivElement>(),
      refMenuCard: createRef<HTMLDivElement>(),
      refModesSubmenu: createRef<HTMLDivElement>(),
      refScoreValue: createRef<HTMLSpanElement>(),
      refObstacleLimitLabel: createRef<HTMLSpanElement>(),
      refSettingsObstacleLimitLabel: createRef<HTMLSpanElement>(),
      refMissileLock: createRef<HTMLDivElement>(),
      refEnemyLock: createRef<HTMLDivElement>(),
      refThreatLock: createRef<HTMLDivElement>(),
    };
    this.hideComponent = this.hideComponent.bind(this);
    this.openWindow = this.openWindow.bind(this);
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.handleDesktopSelect = this.handleDesktopSelect.bind(this);
    this.handleDesktopOpen = this.handleDesktopOpen.bind(this);
  }

  componentDidUpdate(prevProps: AppProps) {
    if (prevProps.pathname !== this.props.pathname) {
      this.setState(visibilityFromPath(this.props.pathname));
    }
  }

  /** Current open windows from the URL (order = stack, last = focused). */
  getOpenSegments(): OpenSegment[] {
    return parseOpenSegments(this.props.pathname);
  }

  /** Push a new path and mirror visibility into state immediately. */
  commitOpenSegments(segments: OpenSegment[]) {
    const path = buildWindowsPath(segments);
    this.setState(visibilityFromPath(path));
    if (path !== this.props.pathname) {
      this.props.navigate(path);
    }
  }

  handleLoadingComplete = () => {
    this.setState({
      loadingComplete: true,
    });

    // Strict Mode / remounted Loading can call this twice — only one WebGL scene.
    if (this.sceneBooted) {
      return;
    }
    this.sceneBooted = true;

    //handle outside click listener
    document.addEventListener('mousedown', this.handleClickOutside);
    // deselect desktop icons on any click outside the icons (incl. Three.js canvas)
    document.addEventListener('mousedown', this.handleDesktopBackgroundClick);

    // THREE JS SCENE (app/boot + arcade runtime/input)
    const boot = createArcadeScene();
    this.renderer = boot.renderer;
    this.boundMouseMove = boot.onDocumentMouseMove;
    document.addEventListener('mousemove', boot.onDocumentMouseMove, false);

    this.sceneEnv = new SceneEnvironment(boot.scene, boot.gridFloor, boot.gridCeiling);
    // Garage terrain only when the mode exposes the picker; otherwise default horizon
    this.sceneEnv.setTerrain(
      isFeatureEnabled('terrainPicker', resolveGameMode(this.state.gameMode))
        ? this.state.terrainId
        : 'horizon',
    );
    this.sceneEnv.apply();

    const arcade = new ArcadeGame(
      {
        scene: boot.scene,
        camera: boot.camera,
        idleCube: boot.idleCube,
        gridMaterials: [boot.gridMaterialFloor, boot.gridMaterialCeiling],
      },
      {
        onPhaseChange: (phase) => {
          this.setState({ gamePhase: phase });
          if (phase === 'playing') {
            this.clearResumeCountdown();
            // Close desktop focus chrome while flying
            this.setState({
              showMenu: false,
              selectedDesktopIcon: null,
              ammoRemaining: MISSILE_MAX,
              railAmmo: RAILGUN_MAX,
              chaffAmmo: CHAFF_MAX,
              activeWeapon: 'missile',
              enemyAlive: false,
              teammateAlive: false,
              immortal: false,
              obstaclesEnabled: true,
              showSettings: false,
              pauseUi: 'none',
              countdown: null,
            });
            // Re-apply sticky sandbox-like settings after each run start
            if (isSandboxLikeMode(this.state.gameMode)) {
              arcade.setObstacleLimit(this.state.obstacleLimit);
              if (isFeatureEnabled('shipPicker', this.state.gameMode)) {
                arcade.setShipId(this.state.shipId);
              } else {
                arcade.setShipId('xwing');
              }
              // Immortal resets each run (default off)
              arcade.setImmortal(false);
            }
            // Session preference: block auto-aim (not reset each run)
            arcade.setBlockAutoAim(this.state.blockAutoAim);
            arcade.setPaused(false);
          } else {
            this.clearResumeCountdown();
            this.setState({ showSettings: false, pauseUi: 'none', countdown: null });
          }
        },
        onGameOver: (scoreMs) => {
          this.setState({ lastScoreMs: scoreMs, showMenu: false });
        },
        onAmmoChange: (remaining) => {
          this.setState({ ammoRemaining: remaining });
        },
        onRailgunAmmoChange: (remaining) => {
          this.setState({ railAmmo: remaining });
        },
        onChaffAmmoChange: (remaining) => {
          this.setState({ chaffAmmo: remaining });
        },
        onWeaponChange: (weapon) => {
          this.setState({ activeWeapon: weapon });
        },
        onEnemyChange: (alive) => {
          this.setState({ enemyAlive: alive });
        },
        onTeammateChange: (alive) => {
          this.setState({ teammateAlive: alive });
        },
        onImmortalChange: (immortal) => {
          this.setState({ immortal });
        },
      },
    );
    this.arcade = arcade;
    // Dev/Playwright hook for verifying damage visuals
    (window as unknown as { __arcade?: ArcadeGame }).__arcade = arcade;

    const input = bindArcadeInput({
      getArcade: () => this.arcade,
      getShellState: () => ({
        showSettings: this.state.showSettings,
        pauseUi: this.state.pauseUi,
      }),
      beginResumeCountdown: () => this.beginResumeCountdown(),
      tryStartArcade: () => {
        void this.tryStartArcade();
      },
      dismissGameOver: () => this.dismissGameOver(),
      hasOpenWindows: () => this.hasOpenWindows(),
      getLastTouchEndAt: () => this.lastTouchEndAt,
      setLastTouchEndAt: (t) => {
        this.lastTouchEndAt = t;
      },
      getMobileTouchStartAt: () => this.mobileTouchStartAt,
      setMobileTouchStartAt: (t) => {
        this.mobileTouchStartAt = t;
      },
    });
    this.disposeArcadeInput = input.dispose;

    const loop = startRenderLoop({
      boot,
      getArcade: () => this.arcade,
      getSceneEnv: () => this.sceneEnv,
      getGameMode: () => resolveGameMode(this.state.gameMode),
      hudRefs: {
        refScoreValue: this.state.refScoreValue,
        refObstacleLimitLabel: this.state.refObstacleLimitLabel,
        refMissileLock: this.state.refMissileLock,
        refEnemyLock: this.state.refEnemyLock,
        refThreatLock: this.state.refThreatLock,
      },
      onFrameScheduled: () => {
        /* rAF id tracked inside startRenderLoop for stop() */
      },
    });
    this.stopRenderLoop = loop.stop;

    const orientation = bindDeviceOrientation({
      getArcade: () => this.arcade,
      mouse: boot.mouse,
    });
    this.disposeOrientation = orientation.dispose;
  };

  /** True when any desktop panel or blog post window is open (blocks arcade start). */
  hasOpenWindows(): boolean {
    return parseOpenSegments(this.props.pathname).length > 0;
  }

  /** Coarse pointer / touch device — show gyro + tap prompts. */
  isTouchDevice(): boolean {
    if (typeof window === 'undefined') return false;
    return (
      window.matchMedia('(pointer: coarse)').matches ||
      navigator.maxTouchPoints > 0 ||
      'ontouchstart' in window
    );
  }

  /**
   * iOS 13+ requires a user-gesture permission for DeviceOrientation.
   * Safe no-op on Android / desktop.
   */
  requestOrientationPermission = async (): Promise<void> => {
    type DOEWithPermission = {
      requestPermission?: () => Promise<'granted' | 'denied' | 'default'>;
    };
    const DOE = DeviceOrientationEvent as unknown as DOEWithPermission;
    if (typeof DOE.requestPermission !== 'function') {
      this.orientationPermission = 'granted';
      return;
    }
    if (this.orientationPermission === 'granted') {
      return;
    }
    try {
      const result = await DOE.requestPermission();
      this.orientationPermission = result === 'granted' ? 'granted' : 'denied';
    } catch {
      this.orientationPermission = 'denied';
    }
  };

  tryStartArcade = async () => {
    if (this.hasOpenWindows()) {
      return;
    }
    if (this.arcade?.isGameOver()) {
      return;
    }
    await this.requestOrientationPermission();
    // Apply selected mode / garage picks before starting the run
    const mode = resolveGameMode(this.state.gameMode);
    if (mode !== this.state.gameMode) {
      this.setState({ gameMode: mode });
    }
    this.arcade?.setSandboxMode(isSandboxLikeMode(mode));
    if (isSandboxLikeMode(mode)) {
      this.arcade?.setObstacleLimit(this.state.obstacleLimit);
      if (isFeatureEnabled('shipPicker', mode)) {
        this.arcade?.setShipId(this.state.shipId);
      } else {
        this.arcade?.setShipId('xwing');
      }
      if (isFeatureEnabled('terrainPicker', mode)) {
        this.sceneEnv?.setTerrain(this.state.terrainId);
      } else {
        this.sceneEnv?.setTerrain('horizon');
      }
    } else {
      this.arcade?.setShipId('xwing');
      this.sceneEnv?.setTerrain('horizon');
    }
    this.arcade?.start();
  };

  /** Close game-over panel and restore the idle desktop (start prompt, icons). */
  dismissGameOver = () => {
    this.clearResumeCountdown();
    this.arcade?.acknowledgeGameOver();
    this.setState({
      gamePhase: 'idle',
      showSettings: false,
      pauseUi: 'none',
      countdown: null,
    });
  };

  clearResumeCountdown = () => {
    if (this.countdownTimer !== null) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  };

  openSettings = () => {
    if (!this.arcade?.isPlaying()) {
      return;
    }
    this.clearResumeCountdown();
    this.arcade.setPaused(true);
    this.setState({
      showSettings: true,
      pauseUi: 'settings',
      countdown: null,
      settingsZ: 50,
    });
  };

  closeSettings = () => {
    if (!this.arcade?.isPlaying()) {
      this.setState({ showSettings: false, pauseUi: 'none', countdown: null });
      return;
    }
    this.clearResumeCountdown();
    this.arcade.setPaused(true);
    // Stay paused until Space → 3-2-1 → resume
    this.setState({ showSettings: false, pauseUi: 'prompt', countdown: null });
  };

  /**
   * HUD pause control: freeze to PAUSED prompt, or start resume countdown when already paused.
   */
  togglePauseFromHud = () => {
    if (!this.arcade?.isPlaying()) {
      return;
    }
    if (this.state.pauseUi === 'countdown') {
      return;
    }
    // Already on PAUSED screen → same as Space (3-2-1)
    if (this.state.pauseUi === 'prompt') {
      this.beginResumeCountdown();
      return;
    }
    // Settings open → close into PAUSED prompt
    if (this.state.showSettings || this.state.pauseUi === 'settings') {
      this.closeSettings();
      return;
    }
    // Running → pause
    this.clearResumeCountdown();
    this.arcade.setPaused(true);
    this.setState({ showSettings: false, pauseUi: 'prompt', countdown: null });
  };

  beginResumeCountdown = () => {
    if (!this.arcade?.isPlaying()) {
      return;
    }
    this.clearResumeCountdown();
    this.arcade.setPaused(true);
    this.setState({ showSettings: false, pauseUi: 'countdown', countdown: 3 });
    this.countdownTimer = setInterval(() => {
      this.setState((s): Pick<AppState, 'pauseUi' | 'countdown'> => {
        if (s.countdown === null || s.countdown <= 1) {
          this.clearResumeCountdown();
          this.arcade?.setPaused(false);
          return { pauseUi: 'none', countdown: null };
        }
        return { pauseUi: 'countdown', countdown: s.countdown - 1 };
      });
    }, 1000);
  };

  /** Toggle open/closed (used by Start menu items). Updates the URL. */
  hideComponent(name: WindowFlagName) {
    const route = FLAG_TO_ROUTE[name];
    this.commitOpenSegments(toggleRouteInList(this.getOpenSegments(), route));
  }

  /** Open a window (or focus it if already open). Updates the URL. */
  openWindow(name: WindowFlagName) {
    const mode = resolveGameMode(this.state.gameMode);
    // Garage panels are Dev-only until promoted in the feature registry
    if (name === 'showHideTerrain' && !isFeatureEnabled('terrainPicker', mode)) {
      return;
    }
    if (name === 'showHideShips' && !isFeatureEnabled('shipPicker', mode)) {
      return;
    }
    const route = FLAG_TO_ROUTE[name];
    this.commitOpenSegments(openRouteInList(this.getOpenSegments(), route));
  }

  handleDesktopSelect(id: string) {
    this.setState({ selectedDesktopIcon: id as DesktopIconId });
  }

  handleDesktopOpen(id: string) {
    const route = DESKTOP_ICON_TO_ROUTE[id as DesktopIconId];
    if (!route) {
      return;
    }
    // Clear selection highlight once the window is opened.
    this.setState({ selectedDesktopIcon: null });
    this.commitOpenSegments(openRouteInList(this.getOpenSegments(), route));
  }

  /** Clear desktop icon selection when clicking anywhere that is not an icon. */
  handleDesktopBackgroundClick = (event: Event) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      return;
    }
    if (target.closest('.desktop-icon')) {
      return;
    }
    if (this.state.selectedDesktopIcon !== null) {
      this.setState({ selectedDesktopIcon: null });
    }
  };

  /** Close (or ignore open requests) from window chrome — URL is source of truth. */
  handleCallback: ParentCallback = (childData, name) => {
    const route = FLAG_TO_ROUTE[name];
    if (!childData) {
      this.commitOpenSegments(closeRouteInList(this.getOpenSegments(), route));
      return;
    }
    this.commitOpenSegments(openRouteInList(this.getOpenSegments(), route));
  };

  MItem(mimage: string, mname: string): ReactNode {
    return (
      <VStack w="100%" h="100%">
        <HStack w="100%">
          <Image w="50px" h="50px" src={mimage} />
          <Text fontSize="16px" fontFamily="BasedPixel" color="black">
            {mname}
          </Text>
        </HStack>
      </VStack>
    );
  }

  /** Focus a panel window: move its route to the end of the path stack. */
  handleCallbackTwo: ParentCallbackTwo = (
    val_skills,
    val_projects,
    val_about,
    val_blogs,
    val_credits,
    val_terrain = 0,
    val_ships = 0,
  ) => {
    let focused: WindowRouteId | null = null;
    if (val_skills) focused = 'about';
    else if (val_projects) focused = 'projects';
    else if (val_about) focused = 'contact';
    else if (val_blogs) focused = 'blogs';
    else if (val_credits) focused = 'credits';
    else if (val_terrain) focused = 'terrain';
    else if (val_ships) focused = 'ships';

    if (!focused) {
      return;
    }

    const current = this.getOpenSegments();
    const alreadyOpen = current.some((s) => s.kind === 'panel' && s.id === focused);
    if (!alreadyOpen) {
      return;
    }

    const last = current[current.length - 1];
    if (last?.kind === 'panel' && last.id === focused) {
      this.setState(visibilityFromPath(buildWindowsPath(current)));
      return;
    }

    this.commitOpenSegments(openRouteInList(current, focused));
  };

  handleClickOutside(event: MouseEvent) {
    if (this.state.showMenu === true) {
      const t = event.target as Node;
      const inMenuBtn = this.state.refMenu.current?.contains(t);
      const inMenuCard = this.state.refMenuCard.current?.contains(t);
      const inModes = this.state.refModesSubmenu.current?.contains(t);
      if (!inMenuBtn && !inMenuCard && !inModes) {
        this.setState({ showMenu: false, showModesSubmenu: false });
      }
    }
  }

  selectGameMode = (mode: GameModeId) => {
    const next = resolveGameMode(mode);
    this.setState(
      {
        gameMode: next,
        showModesSubmenu: false,
        showMenu: false,
        // Garage windows only make sense in modes that expose the pickers
        showHideTerrain: isFeatureEnabled('terrainPicker', next)
          ? this.state.showHideTerrain
          : false,
        showHideShips: isFeatureEnabled('shipPicker', next) ? this.state.showHideShips : false,
      },
      () => {
        this.arcade?.setSandboxMode(isSandboxLikeMode(next));
        if (isFeatureEnabled('terrainPicker', next)) {
          this.sceneEnv?.setTerrain(this.state.terrainId);
        } else {
          this.sceneEnv?.setTerrain('horizon');
        }
        if (isFeatureEnabled('shipPicker', next)) {
          this.arcade?.setShipId(this.state.shipId);
        } else {
          this.arcade?.setShipId('xwing');
        }
      },
    );
  };

  selectTerrain = (id: TerrainId) => {
    this.setState({ terrainId: id });
    this.sceneEnv?.setTerrain(id);
  };

  selectShip = (id: ShipId) => {
    this.setState({ shipId: id });
    this.arcade?.setShipId(id);
  };

  componentDidMount() {
    // Start the clock interval
    this.clockInterval = setInterval(this.updateClock, 1000);
  }

  componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside);
    document.removeEventListener('mousedown', this.handleDesktopBackgroundClick);
    if (this.boundMouseMove) {
      document.removeEventListener('mousemove', this.boundMouseMove);
      this.boundMouseMove = null;
    }
    this.disposeArcadeInput?.();
    this.disposeArcadeInput = null;
    this.disposeOrientation?.();
    this.disposeOrientation = null;
    this.stopRenderLoop?.();
    this.stopRenderLoop = null;
    this.clearResumeCountdown();
    this.arcade?.dispose();
    this.arcade = null;
    if (this.renderer) {
      this.renderer.domElement.remove();
      this.renderer.dispose();
      this.renderer = null;
    }
    this.sceneEnv = null;
    this.sceneBooted = false;
    if (this.clockInterval !== null) {
      clearInterval(this.clockInterval);
    }
  }

  updateClock = () => {
    this.setState({ currentTime: new Date() });
  };

  render() {
    const {
      loadingComplete,
      currentTime,
      showHideSkills,
      showHideProjects,
      showHideAbout,
      showHideBlogs,
      showHideCredits,
      showHideTerrain,
      showHideShips,
      openBlogSlugs,
      gamePhase,
      gameMode,
      lastScoreMs,
      ammoRemaining,
      railAmmo,
      chaffAmmo,
      activeWeapon,
      enemyAlive,
      enemySpawnCount,
      teammateAlive,
      teammateSpawnCount,
      immortal,
      blockAutoAim,
      showSettings,
      pauseUi,
      countdown,
      settingsZ,
      obstaclesEnabled,
      obstacleLimit,
      terrainId,
      shipId,
      z_skills,
      z_about,
      z_projects,
      z_blogs,
      z_credits,
      z_terrain,
      z_ships,
    } = this.state;

    const isPlaying = gamePhase === 'playing';
    const isGameOver = gamePhase === 'gameover';
    const mode = resolveGameMode(gameMode);
    const sandboxLike = isSandboxLikeMode(mode);
    const badge = modeBadgeLabel(mode);
    const showTerrainPicker = isFeatureEnabled('terrainPicker', mode);
    const showShipPicker = isFeatureEnabled('shipPicker', mode);
    const showSandboxTools = isFeatureEnabled('sandboxTools', mode);
    const showEnemySpawn = isFeatureEnabled('enemySpawn', mode);
    const showTeammateSpawn = isFeatureEnabled('teammateSpawn', mode);
    const showImmortal = isFeatureEnabled('immortal', mode);
    const showObstacleControls = isFeatureEnabled('obstacleControls', mode);
    const windowsOpen = this.hasOpenWindows();
    const showStartPrompt = !isPlaying && !isGameOver && !windowsOpen;
    const ammoIcons = Array.from({ length: MISSILE_MAX }, (_, i) => i < ammoRemaining);
    const modesList = availableModes();

    return (
      <div>
        {!loadingComplete ? (
          <Loading onLoadingComplete={this.handleLoadingComplete} />
        ) : (
          <div
            className={[
              'App',
              isPlaying ? 'App--arcade-playing' : '',
              isGameOver ? 'App--arcade-gameover' : '',
            ]
              .filter(Boolean)
              .join(' ')}
          >
            <div ref={(ref) => (this.mount = ref)} />

            {showStartPrompt ? (
              <p className="arcade-start-prompt">
                {this.isTouchDevice() ? 'Tap to start' : 'Press space to start'}
              </p>
            ) : null}

            {/* Mode idle mark — over the home cube, corner-bracket frame only */}
            {badge && !isPlaying && !isGameOver ? (
              <div
                className={[
                  'arcade-sandbox-home-label',
                  badge === 'DEV' ? 'arcade-sandbox-home-label--dev' : '',
                ]
                  .filter(Boolean)
                  .join(' ')}
                aria-label={`${badge} mode`}
              >
                <span className="arcade-sandbox-home-label__corner arcade-sandbox-home-label__corner--tl" />
                <span className="arcade-sandbox-home-label__corner arcade-sandbox-home-label__corner--tr" />
                <span className="arcade-sandbox-home-label__corner arcade-sandbox-home-label__corner--bl" />
                <span className="arcade-sandbox-home-label__corner arcade-sandbox-home-label__corner--br" />
                <span className="arcade-sandbox-home-label__text">{badge}</span>
              </div>
            ) : null}

            {!isPlaying && !isGameOver && (showTerrainPicker || showShipPicker) ? (
              <div className="arcade-sandbox-pickers">
                {showTerrainPicker ? (
                  <button
                    type="button"
                    className="arcade-sandbox-picker-btn"
                    onClick={() => this.openWindow('showHideTerrain')}
                  >
                    <span className="arcade-sandbox-picker-btn__kind">TERRAIN</span>
                    <span className="arcade-sandbox-picker-btn__value">
                      {terrainLabel(terrainId)}
                    </span>
                  </button>
                ) : null}
                {showShipPicker ? (
                  <button
                    type="button"
                    className="arcade-sandbox-picker-btn"
                    onClick={() => this.openWindow('showHideShips')}
                  >
                    <span className="arcade-sandbox-picker-btn__kind">SPACESHIP</span>
                    <span className="arcade-sandbox-picker-btn__value">{shipLabel(shipId)}</span>
                  </button>
                ) : null}
              </div>
            ) : null}

            {isPlaying ? (
              <ArcadeHud
                sandboxLike={sandboxLike}
                activeWeapon={activeWeapon}
                missileLabel={playerMissileLabel(shipId)}
                secondaryWeaponLabel={playerSecondaryWeaponLabel(shipId)}
                countermeasureLabel={playerCountermeasureLabel(shipId)}
                ammoIcons={ammoIcons}
                railAmmo={railAmmo}
                chaffAmmo={chaffAmmo}
                missileMax={MISSILE_MAX}
                railMax={RAILGUN_MAX}
                chaffMax={CHAFF_MAX}
                badge={badge}
                pauseUi={pauseUi}
                showSettings={showSettings}
                showSandboxTools={showSandboxTools}
                showEnemySpawn={showEnemySpawn}
                showTeammateSpawn={showTeammateSpawn}
                showImmortal={showImmortal}
                showObstacleControls={showObstacleControls}
                enemyAlive={enemyAlive}
                enemySpawnCount={enemySpawnCount}
                enemySpawnMax={ENEMY_SPAWN_MAX}
                teammateAlive={teammateAlive}
                teammateSpawnCount={teammateSpawnCount}
                teammateSpawnMax={TEAMMATE_SPAWN_MAX}
                immortal={immortal}
                blockAutoAim={blockAutoAim}
                obstaclesEnabled={obstaclesEnabled}
                obstacleLimit={obstacleLimit}
                obstacleLimitMin={OBSTACLE_LIMIT_MIN}
                obstacleLimitMax={OBSTACLE_LIMIT_MAX}
                refScoreValue={this.state.refScoreValue}
                refObstacleLimitLabel={this.state.refObstacleLimitLabel}
                refMissileLock={this.state.refMissileLock}
                refEnemyLock={this.state.refEnemyLock}
                refThreatLock={this.state.refThreatLock}
                onSelectMissile={() => this.arcade?.setWeapon('missile')}
                onSelectRailgun={() => this.arcade?.setWeapon('railgun')}
                onSelectChaff={() => this.arcade?.setWeapon('chaff')}
                onTogglePause={() => this.togglePauseFromHud()}
                onToggleSettings={() => {
                  if (this.state.showSettings) {
                    this.closeSettings();
                  } else {
                    this.openSettings();
                  }
                }}
                onEnemySpawnCount={(n) => this.setState({ enemySpawnCount: n })}
                onTeammateSpawnCount={(n) => this.setState({ teammateSpawnCount: n })}
                onSpawnEnemy={() => this.arcade?.spawnEnemy(this.state.enemySpawnCount)}
                onSpawnTeammate={() => this.arcade?.spawnTeammate(this.state.teammateSpawnCount)}
                onToggleImmortal={() => {
                  const next = !this.state.immortal;
                  this.arcade?.setImmortal(next);
                  this.setState({ immortal: next });
                }}
                onToggleObstacles={() => {
                  const next = !this.state.obstaclesEnabled;
                  this.arcade?.setObstaclesEnabled(next);
                  this.setState({ obstaclesEnabled: next });
                }}
                onObstacleLimit={(n) => {
                  this.arcade?.setObstacleLimit(n);
                  this.setState({ obstacleLimit: n });
                }}
                getObstacleCount={() => this.arcade?.getObstacleCount() ?? 0}
              />
            ) : null}

            {isPlaying ? <PauseOverlay phase={pauseUi} countdown={countdown} /> : null}

            {isPlaying && showSettings ? (
              <Settings
                zIndex={settingsZ}
                onClose={this.closeSettings}
                onFocus={() => this.setState({ settingsZ: 50 })}
                gameTools={{
                  mode,
                  enemyAlive,
                  enemySpawnCount,
                  teammateAlive,
                  teammateSpawnCount,
                  immortal,
                  blockAutoAim,
                  obstaclesEnabled,
                  obstacleLimit,
                  obstacleLimitLabelRef: this.state.refSettingsObstacleLimitLabel,
                  onEnemySpawnCount: (n) => this.setState({ enemySpawnCount: n }),
                  onTeammateSpawnCount: (n) => this.setState({ teammateSpawnCount: n }),
                  onSpawnEnemy: () => this.arcade?.spawnEnemy(this.state.enemySpawnCount),
                  onSpawnTeammate: () => this.arcade?.spawnTeammate(this.state.teammateSpawnCount),
                  onToggleImmortal: () => {
                    const next = !this.state.immortal;
                    this.arcade?.setImmortal(next);
                    this.setState({ immortal: next });
                  },
                  onToggleBlockAutoAim: () => {
                    const next = !this.state.blockAutoAim;
                    this.arcade?.setBlockAutoAim(next);
                    this.setState({ blockAutoAim: next });
                  },
                  onToggleObstacles: () => {
                    const next = !this.state.obstaclesEnabled;
                    this.arcade?.setObstaclesEnabled(next);
                    this.setState({ obstaclesEnabled: next });
                  },
                  onObstacleLimit: (n) => {
                    this.arcade?.setObstacleLimit(n);
                    this.setState({ obstacleLimit: n });
                    const label = this.state.refObstacleLimitLabel.current;
                    if (label && this.arcade) {
                      label.textContent = `BLOCKS ${this.arcade.getObstacleCount()}/${n}`;
                    }
                  },
                  getObstacleCount: () => this.arcade?.getObstacleCount() ?? 0,
                }}
              />
            ) : null}

            {isPlaying && this.isTouchDevice() ? (
              <p className="arcade-tilt-hint">tilt · tap fire · hold 3s lock · HUD switch</p>
            ) : null}

            {isGameOver ? (
              <GameOverWindow scoreMs={lastScoreMs} onDismiss={this.dismissGameOver} />
            ) : null}

            {showHideSkills && (
              <Skills
                parentCallback={this.handleCallback}
                data={z_skills}
                parentCallbackTwo={this.handleCallbackTwo}
              />
            )}
            {showHideProjects && (
              <Projects
                parentCallback={this.handleCallback}
                data={z_projects}
                parentCallbackTwo={this.handleCallbackTwo}
              />
            )}
            {showHideAbout && (
              <Contact
                parentCallback={this.handleCallback}
                data={z_about}
                parentCallbackTwo={this.handleCallbackTwo}
              />
            )}
            {showHideBlogs && (
              <Blogs
                parentCallback={this.handleCallback}
                data={z_blogs}
                parentCallbackTwo={this.handleCallbackTwo}
              />
            )}
            {openBlogSlugs.map((slug) => (
              <BlogPostWindow key={slug} slug={slug} />
            ))}
            {showHideCredits && (
              <Credits
                parentCallback={this.handleCallback}
                data={z_credits}
                parentCallbackTwo={this.handleCallbackTwo}
              />
            )}
            {showHideTerrain && showTerrainPicker ? (
              <TerrainPicker
                parentCallback={this.handleCallback}
                data={z_terrain}
                parentCallbackTwo={this.handleCallbackTwo}
                selectedId={terrainId}
                onSelect={this.selectTerrain}
              />
            ) : null}
            {showHideShips && showShipPicker ? (
              <ShipsPicker
                parentCallback={this.handleCallback}
                data={z_ships}
                parentCallbackTwo={this.handleCallbackTwo}
                selectedId={shipId}
                onSelect={this.selectShip}
              />
            ) : null}

            <DesktopShell
              currentTime={currentTime}
              showMenu={this.state.showMenu}
              showModesSubmenu={this.state.showModesSubmenu}
              selectedDesktopIcon={this.state.selectedDesktopIcon}
              gameMode={mode}
              modesList={modesList}
              icons={DESKTOP_ICONS}
              refMenu={this.state.refMenu}
              refMenuCard={this.state.refMenuCard}
              refModesSubmenu={this.state.refModesSubmenu}
              onToggleMenu={() => {
                this.setState((s) => ({
                  showMenu: !s.showMenu,
                  showModesSubmenu: false,
                }));
              }}
              onToggleModesSubmenu={() => {
                this.setState((s) => ({ showModesSubmenu: !s.showModesSubmenu }));
              }}
              onSelectMode={this.selectGameMode}
              onOpenMenuItem={(flag) => {
                this.hideComponent(flag);
                this.setState({ showMenu: false, showModesSubmenu: false });
              }}
              onDesktopSelect={this.handleDesktopSelect}
              onDesktopOpen={this.handleDesktopOpen}
              modeMenuLabel={modeMenuLabel}
            />
          </div>
        )}
      </div>
    );
  }
}

/** Router-aware entry used by `index.tsx`. */
export function AppWithRouter() {
  const navigate = useNavigate();
  const location = useLocation();
  return <App navigate={navigate} pathname={location.pathname} />;
}

export default AppWithRouter;

//TODO: Add screen rotation (for mobile)
//TODO: Add mobile gyro :DONE
//TODO: UI bug fixes - Fucking list them down!
//TODO: Add loading screen

/* 

  FUTURE IDEAS:
      1. Create an THYM-LLM chat app
        1.1 something that talks like me?
        1.2 something that given a code file / repo - given problem - will generate code according
      2. Create new Window for TRASH
        2.1 Store ideas will work on future
        2.2 Card format when clicked shows detailed idea
      3. 

*/
